import { type ComponentType, useEffect, useMemo, useState } from "react"
import { Head, Link, usePage } from "@inertiajs/react"
import {
  ChevronDown,
  ChevronRight,
  Ellipsis,
  ExternalLink,
  LayoutGrid,
  List as ListIcon,
  ListTodo,
  MessageSquare,
  Paperclip,
  Plus,
  Tag as TagIcon,
  TriangleAlert,
  X,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { ChannelFilters } from "@/components/calendar/ChannelFilters"
import { PiecePreviewProvider, usePiecePreview } from "@/components/calendar/PiecePreviewModal"
import { NewPieceModal } from "@/components/NewPieceModal"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useFormatDateTime } from "@/lib/datetime"
import { isSchedulePastDue } from "@/lib/scheduling"
import { PastDueWarning } from "@/components/scheduling/PastDueWarning"
import { CompactPieceCard } from "@/components/pieces/CompactPieceCard"
import { cn } from "@/lib/utils"
import { filterTriggerClasses } from "@/lib/filter-trigger"
import { useStoredState } from "@/lib/use-stored-state"
import type { PageProps } from "@/types/inertia"
import { PipelineSubNav } from "./PipelineSubNav"
import { useActionMenu, type ActionKind } from "@/lib/use-action-menu"
import { useFireAction } from "@/lib/use-fire-action"
import { todoLabel } from "@/lib/todo-labels"

type Piece = {
  id: number
  name: string
  channel: string
  channel_name: string | null
  channel_type: string | null
  channel_color: string | null
  display_title: string | null
  body_text: string | null
  publishing_status: string
  created_at: string
  publish_at: string | null
  published_url: string | null
  published_at: string | null
  archived_at: string | null
  failed_reason: string | null
  comment_count: number
  attachment_count: number
  thumbnail_url: string | null
  video_url: string | null
  video_filename: string | null
  open_todos: Array<{ assignee: string; kind: string }>
  slot: {
    publish_at: string
    upload_at: string | null
    use_platform_scheduling: boolean
    manual_publish: boolean
    publishing_at: string | null
  } | null
  tags: Array<{ id: number; name: string; color: string; icon: string }>
}

type Channel = {
  slug: string
  name: string
  channel_type: string
  color: string
  credentials_status: string
  publishes_manually?: boolean
  allow_platform_scheduling?: boolean
  platform_scheduling_lead_hours?: number
  default_to_platform_scheduling?: boolean
  supports_platform_scheduling?: boolean
  forces_platform_scheduling?: boolean
}

// A sub-section within a phase column — matches one publishing lane.
// `match` decides whether a piece belongs here.
type SubDef = {
  key: string
  label: string
  match: (piece: Piece) => boolean
}

// A phase column groups one or more sub-sections under a single header.
// When `subs` has a single entry the sub-header is suppressed (the column
// header carries the count).
type PhaseDef = {
  key: string
  label: string
  subs: SubDef[]
}

const PHASES: PhaseDef[] = [
  {
    key: "pending",
    label: "Pending",
    subs: [
      {
        key: "pending",
        label: "Pending",
        match: (p) => p.publishing_status === "pending",
      },
    ],
  },
  {
    key: "queued_to_schedule",
    label: "Queued to schedule",
    subs: [
      {
        key: "queued_to_schedule",
        label: "Queued to schedule",
        match: (p) => p.publishing_status === "queued_to_schedule",
      },
    ],
  },
  {
    key: "queued_to_publish",
    label: "To publish manually",
    subs: [
      {
        key: "queued_to_publish",
        label: "To publish manually",
        match: (p) => p.publishing_status === "queued_to_publish",
      },
    ],
  },
  {
    key: "scheduled",
    label: "Scheduled",
    subs: [
      {
        key: "scheduled",
        label: "Scheduled",
        match: (p) => p.publishing_status === "scheduled",
      },
    ],
  },
  {
    key: "published",
    label: "Published",
    subs: [
      {
        key: "published",
        label: "Published",
        match: (p) => p.publishing_status === "published",
      },
    ],
  },
]

// The manual-publish lane. Its board column auto-collapses when empty.
const MANUAL_PHASE_KEY = "queued_to_publish"

type PipelineView = "board" | "list"

const PIPELINE_VIEW_STORAGE_KEY = "sparkdrop:pipeline:view"
const PIPELINE_LIST_COLLAPSED_STORAGE_KEY = "sparkdrop:pipeline:list-collapsed"
const PIPELINE_TAG_FILTER_STORAGE_KEY = "sparkdrop:pipeline:filter:tag"
const PIPELINE_CHANNEL_FILTER_STORAGE_KEY = "sparkdrop:pipeline:filter:channels"

// By default, only the first phase ("pending") is expanded in list view; the
// rest start collapsed.
const DEFAULT_LIST_COLLAPSED = PHASES.slice(1).map((p) => p.key)

// Remembers the last view (board / list) per-browser. Starts on "board" for a
// deterministic SSR render, then restores the saved view on the client.
function usePipelineView() {
  const [view, setView] = useState<PipelineView>("board")
  useEffect(() => {
    if (typeof window === "undefined") return
    try {
      const stored = window.localStorage.getItem(PIPELINE_VIEW_STORAGE_KEY)
      if (stored === "board" || stored === "list") setView(stored)
    } catch {
      // localStorage unavailable (private mode / blocked) — non-fatal.
    }
  }, [])
  useEffect(() => {
    if (typeof window === "undefined") return
    try {
      window.localStorage.setItem(PIPELINE_VIEW_STORAGE_KEY, view)
    } catch {
      // non-fatal
    }
  }, [view])
  return [view, setView] as const
}

// Remembers which list-view sections are collapsed per-browser. Defaults to
// every phase except the first collapsed; restores the saved set on the client.
function useListCollapsed() {
  const [collapsed, setCollapsed] = useState<Set<string>>(
    () => new Set(DEFAULT_LIST_COLLAPSED),
  )
  useEffect(() => {
    if (typeof window === "undefined") return
    try {
      const raw = window.localStorage.getItem(PIPELINE_LIST_COLLAPSED_STORAGE_KEY)
      if (!raw) return
      const arr = JSON.parse(raw)
      if (Array.isArray(arr)) {
        setCollapsed(new Set(arr.filter((x): x is string => typeof x === "string")))
      }
    } catch {
      // ignore malformed / unavailable storage
    }
  }, [])
  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      try {
        window.localStorage.setItem(
          PIPELINE_LIST_COLLAPSED_STORAGE_KEY,
          JSON.stringify([...next]),
        )
      } catch {
        // non-fatal
      }
      return next
    })
  return [collapsed, toggle] as const
}

export default function Pipeline({
  pieces,
  channels,
  failed_count,
}: {
  pieces: Piece[]
  channels: Channel[]
  failed_count: number
}) {
  const channelsBySlug = useMemo(() => {
    const m = new Map<string, Channel>()
    for (const c of channels) m.set(c.slug, c)
    return m
  }, [channels])
  const { props } = usePage<PageProps>()

  // Pieces that belong on a main kanban column — failed / backburner /
  // archived are intentionally NOT shown as columns.
  const KANBAN_STATUSES = ["pending", "queued_to_schedule", "queued_to_publish", "scheduled", "published"]
  const visiblePieces = pieces.filter((p) => KANBAN_STATUSES.includes(p.publishing_status))

  // Tag filter — client-side. The full pipeline lives in memory already,
  // so a dropdown filter is faster than a server round-trip.
  const tagChoices = useMemo(() => {
    const seen = new Map<number, string>()
    for (const p of visiblePieces) {
      for (const t of p.tags) {
        if (!seen.has(t.id)) seen.set(t.id, t.name)
      }
    }
    return Array.from(seen.entries())
      .map(([ id, name ]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [visiblePieces])

  const [tagFilter, setTagFilter] = useStoredState<number | null>(
    PIPELINE_TAG_FILTER_STORAGE_KEY,
    null,
  )
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set())
  // The manual lane auto-minimizes when empty and opens when it has pieces,
  // until the user collapses/expands it by hand (then we honor their choice).
  const [manualUserToggled, setManualUserToggled] = useState(false)
  const [view, setView] = usePipelineView()
  const [listCollapsed, toggleListCollapsed] = useListCollapsed()

  // Channel filter — same dropdown/UI as the calendar. Client-side, like the
  // tag filter above. Starts with every channel visible.
  const sortedChannels = useMemo(
    () => [...channels].sort((a, b) => a.name.localeCompare(b.name)),
    [channels],
  )
  const [visibleChannels, setVisibleChannels] = useStoredState<Set<string>>(
    PIPELINE_CHANNEL_FILTER_STORAGE_KEY,
    new Set(channels.map((c) => c.slug)),
    {
      serialize: (s) => JSON.stringify([...s]),
      deserialize: (raw) => new Set(JSON.parse(raw) as string[]),
    },
  )
  const toggleChannel = (slug: string) =>
    setVisibleChannels((prev) => {
      const next = new Set(prev)
      next.has(slug) ? next.delete(slug) : next.add(slug)
      return next
    })
  const setAllChannels = (visible: boolean) =>
    setVisibleChannels(visible ? new Set(channels.map((c) => c.slug)) : new Set())

  const toggle = (key: string) => {
    if (key === MANUAL_PHASE_KEY) setManualUserToggled(true)
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const filteredPieces = visiblePieces.filter((p) => {
    if (tagFilter != null && !p.tags.some((t) => t.id === tagFilter)) return false
    if (!visibleChannels.has(p.channel)) return false
    return true
  })

  // Bucket every visible piece into its sub-section.
  const bySub = useMemo(() => {
    const map = new Map<string, Piece[]>()
    for (const phase of PHASES) {
      for (const sub of phase.subs) {
        const matched = filteredPieces.filter((p) => sub.match(p))
        // Per-sub sort: keep the next-to-touch up top.
        if (sub.key === "scheduled") {
          matched.sort((a, b) => (a.publish_at ?? "").localeCompare(b.publish_at ?? ""))
        } else if (sub.key === "published") {
          matched.sort((a, b) =>
            (b.publish_at ?? b.created_at).localeCompare(a.publish_at ?? a.created_at),
          )
        }
        map.set(`${phase.key}:${sub.key}`, matched)
      }
    }
    return map
  }, [filteredPieces])

  // The manual lane minimizes itself when empty (until the user toggles it).
  const manualCount = bySub.get(`${MANUAL_PHASE_KEY}:${MANUAL_PHASE_KEY}`)?.length ?? 0
  const isCollapsed = (key: string) => {
    if (key === MANUAL_PHASE_KEY && !manualUserToggled) return manualCount === 0
    return collapsed.has(key)
  }

  const activeTag = tagFilter != null ? tagChoices.find((g) => g.id === tagFilter) : null
  // Channels are "filtered" when at least one is hidden. Combined with the
  // tag filter, this drives the Clear-filters affordance.
  const channelsFiltered = sortedChannels.some((c) => !visibleChannels.has(c.slug))
  const filtersActive = tagFilter != null || channelsFiltered
  const clearFilters = () => {
    setTagFilter(null)
    setAllChannels(true)
  }

  return (
    <>
      <Head title="Pipeline">
        <meta
          name="description"
          content="Kanban view of every content piece in SparkDrop, organized by lifecycle stage."
        />
        <meta property="og:title" content="Pipeline" />
        <meta
          property="og:description"
          content="Kanban view of every content piece in SparkDrop, organized by lifecycle stage."
        />
      </Head>
      <AppShell wide="wider">
        <div className="flex items-end justify-between border-b border-hairline pb-6">
          <h1>Pipeline</h1>
          <div className="flex items-center gap-4">
            {failed_count > 0 && (
              <Link
                href="/pipeline/failed"
                className="inline-flex items-center gap-1.5 text-sm text-danger-display no-underline hover:text-danger-display/80"
              >
                <TriangleAlert className="h-3.5 w-3.5" />
                Failed posts
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-danger-faded px-1.5 text-xs font-medium text-danger-display">
                  {failed_count}
                </span>
              </Link>
            )}
            {props.new_piece_options && (
              <NewPieceModal
                trigger={
                  <Button>
                    <Plus className="h-4 w-4" />
                    New piece
                  </Button>
                }
              />
            )}
          </div>
        </div>

        <PipelineSubNav active="pipeline" />

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className={filterTriggerClasses(activeTag != null)}>
                <TagIcon
                  className={cn(
                    "h-3.5 w-3.5",
                    activeTag ? "text-accent" : "text-ink-muted",
                  )}
                />
                <span>Tag:</span>
                <span className={activeTag ? "text-accent-display" : "text-ink-display"}>
                  {activeTag ? activeTag.name : "All"}
                </span>
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5",
                    activeTag ? "text-accent" : "text-ink-muted",
                  )}
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
              <DropdownMenuItem onSelect={() => setTagFilter(null)}>
                All tags
              </DropdownMenuItem>
              {tagChoices.map((g) => (
                <DropdownMenuItem key={g.id} onSelect={() => setTagFilter(g.id)}>
                  {g.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <ChannelFilters
            channels={sortedChannels}
            visible={visibleChannels}
            onToggle={toggleChannel}
            onSetAll={setAllChannels}
          />
          {filtersActive && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 text-xs text-accent hover:text-accent-display"
            >
              <X className="h-3 w-3" />
              Clear filters
            </button>
          )}
          <ViewToggle view={view} onChange={setView} className="ml-auto" />
        </div>

        <PiecePreviewProvider>
          {view === "board" ? (
            <div className="mt-6 flex gap-4 overflow-x-auto pb-4">
              {PHASES.map((phase) => (
                <PhaseColumn
                  key={phase.key}
                  phase={phase}
                  bySub={bySub}
                  channelsBySlug={channelsBySlug}
                  isCollapsed={isCollapsed}
                  onToggle={toggle}
                />
              ))}
            </div>
          ) : (
            <PipelineListView
              phases={PHASES}
              bySub={bySub}
              channelsBySlug={channelsBySlug}
              collapsed={listCollapsed}
              onToggle={toggleListCollapsed}
            />
          )}
        </PiecePreviewProvider>
      </AppShell>
    </>
  )
}

// Segmented board/list switch, styled to match the theme-toggle primitive.
function ViewToggle({
  view,
  onChange,
  className,
}: {
  view: PipelineView
  onChange: (next: PipelineView) => void
  className?: string
}) {
  const OPTIONS: Array<{
    value: PipelineView
    label: string
    Icon: ComponentType<{ className?: string }>
  }> = [
    { value: "board", label: "Board", Icon: LayoutGrid },
    { value: "list", label: "List", Icon: ListIcon },
  ]
  return (
    <div
      role="radiogroup"
      aria-label="Pipeline view"
      className={cn(
        "inline-flex items-center gap-0.5 rounded-md border border-hairline bg-surface p-0.5",
        className,
      )}
    >
      {OPTIONS.map(({ value, label, Icon }) => {
        const isActive = view === value
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={`${label} view`}
            title={`${label} view`}
            onClick={() => onChange(value)}
            className={cn(
              "inline-flex h-7 cursor-pointer items-center gap-1.5 rounded px-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
              isActive
                ? "bg-page text-ink-display"
                : "text-ink-muted hover:text-ink-display",
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        )
      })}
    </div>
  )
}

// Mobile-optimized stacked view: every phase is a full-width, collapsible
// section with an <h3> header and a count. Collapse state is persisted.
function PipelineListView({
  phases,
  bySub,
  channelsBySlug,
  collapsed,
  onToggle,
}: {
  phases: PhaseDef[]
  bySub: Map<string, Piece[]>
  channelsBySlug: Map<string, Channel>
  collapsed: Set<string>
  onToggle: (key: string) => void
}) {
  return (
    <div className="mt-6 flex flex-col gap-3 pb-4">
      {phases.map((phase) => {
        const pieces = phase.subs.flatMap(
          (sub) => bySub.get(`${phase.key}:${sub.key}`) ?? [],
        )
        const isCollapsed = collapsed.has(phase.key)
        const Chevron = isCollapsed ? ChevronRight : ChevronDown
        const headingId = `list-phase-${phase.key}-heading`
        const bodyId = `list-phase-${phase.key}-body`
        return (
          <section
            key={phase.key}
            aria-labelledby={headingId}
            className="rounded-lg bg-surface"
          >
            <button
              type="button"
              onClick={() => onToggle(phase.key)}
              aria-expanded={!isCollapsed}
              aria-controls={bodyId}
              className="flex w-full items-center gap-2 rounded-lg px-4 py-3 text-left hover:bg-page/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <Chevron className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden="true" />
              <h3 id={headingId} className="flex-1">
                {phase.label}
              </h3>
              <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full border border-hairline px-1.5 text-xs font-medium tabular-nums text-ink-muted">
                {pieces.length}
              </span>
            </button>
            {!isCollapsed && (
              <ul id={bodyId} className="flex flex-col gap-2 px-3 pb-3">
                {pieces.length === 0 ? (
                  <li className="px-1 py-4 text-center text-xs text-ink-muted">
                    no pieces here
                  </li>
                ) : (
                  pieces.map((piece) => (
                    <PieceCard
                      key={piece.id}
                      piece={piece}
                      phaseKey={phase.key}
                      channel={channelsBySlug.get(piece.channel)}
                      expand="flow"
                    />
                  ))
                )}
              </ul>
            )}
          </section>
        )
      })}
    </div>
  )
}

// Collapsible phase column matching Tubery's kanban style: chevron +
// uppercase label + horizontal rule + count circle. When the phase has
// more than one sub-section, each renders as its own collapsible
// sub-list with its own chevron/label/rule/count.
function PhaseColumn({
  phase,
  bySub,
  channelsBySlug,
  isCollapsed,
  onToggle,
}: {
  phase: PhaseDef
  bySub: Map<string, Piece[]>
  channelsBySlug: Map<string, Channel>
  isCollapsed: (key: string) => boolean
  onToggle: (key: string) => void
}) {
  const total = phase.subs.reduce(
    (sum, sub) => sum + (bySub.get(`${phase.key}:${sub.key}`)?.length ?? 0),
    0,
  )
  const collapsed = isCollapsed(phase.key)
  const Chevron = collapsed ? ChevronRight : ChevronDown
  const headingId = `phase-${phase.key}-heading`
  const bodyId = `phase-${phase.key}-body`
  const showSubHeaders = phase.subs.length > 1

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        "flex flex-col rounded-lg bg-surface",
        collapsed ? "w-auto shrink-0" : "min-w-0 flex-1 basis-0",
      )}
    >
      <header className="flex items-center gap-2 px-3 py-3">
        <button
          type="button"
          onClick={() => onToggle(phase.key)}
          aria-expanded={!collapsed}
          aria-controls={bodyId}
          className="inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 text-left text-ink-display hover:bg-page/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <Chevron className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span
            id={headingId}
            role="heading"
            aria-level={2}
            className="font-display text-xs font-semibold uppercase tracking-wider"
          >
            {phase.label}
          </span>
        </button>
        <span aria-hidden="true" className="flex-1 border-t border-hairline" />
        <span className="-ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full border border-hairline text-[10px] font-medium tabular-nums text-ink-muted">
          {total}
        </span>
      </header>

      <div id={bodyId} hidden={collapsed} className="flex flex-col gap-3 p-3 pt-0">
        {phase.subs.map((sub) => (
          <SubList
            key={sub.key}
            phaseKey={phase.key}
            sub={sub}
            pieces={bySub.get(`${phase.key}:${sub.key}`) ?? []}
            channelsBySlug={channelsBySlug}
            collapsible={showSubHeaders}
            collapsed={showSubHeaders && isCollapsed(`${phase.key}:${sub.key}`)}
            onToggle={() => onToggle(`${phase.key}:${sub.key}`)}
          />
        ))}
      </div>
    </section>
  )
}

function SubList({
  phaseKey,
  sub,
  pieces,
  channelsBySlug,
  collapsible,
  collapsed,
  onToggle,
}: {
  phaseKey: string
  sub: SubDef
  pieces: Piece[]
  channelsBySlug: Map<string, Channel>
  collapsible: boolean
  collapsed: boolean
  onToggle: () => void
}) {
  const Chevron = collapsed ? ChevronRight : ChevronDown
  const cards =
    pieces.length === 0 ? (
      <li className="px-1 py-4 text-center text-xs text-ink-muted">no pieces here</li>
    ) : (
      pieces.map((piece) => (
        <PieceCard
          key={piece.id}
          piece={piece}
          phaseKey={phaseKey}
          channel={channelsBySlug.get(piece.channel)}
          expand="overlay"
          // The last board column's overlays grow leftward so they never
          // run off the right edge of the board.
          expandAlign={phaseKey === "published" ? "right" : "left"}
        />
      ))
    )

  if (!collapsible) {
    return <ul className="flex flex-col gap-2">{cards}</ul>
  }

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="flex items-center gap-2 rounded-md px-1 py-0.5 text-left text-ink-body hover:bg-page/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <span className="inline-flex items-center gap-1.5">
          <Chevron className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="text-[10px] font-semibold uppercase tracking-wider">{sub.label}</span>
        </span>
        <span aria-hidden="true" className="flex-1 border-t border-hairline" />
        <span className="-ml-2 inline-flex h-4 w-4 items-center justify-center rounded-full border border-hairline text-[9px] font-medium tabular-nums">
          {pieces.length}
        </span>
      </button>
      {!collapsed && <ul className="flex flex-col gap-2">{cards}</ul>}
    </div>
  )
}

// Adapter over the shared CompactPieceCard so pipeline cards look and behave
// exactly like calendar cards: standard collapsed height (channel icon +
// time-or-channel on line 1, two clamped lines of text), hover expansion
// (overlay on the board, in-flow in the list) with the full text, media
// icon, pipeline extras (actions menu, todos, counts, published link), and
// the piece's tags. Clicking opens the same piece-preview modal.
function PieceCard({
  piece,
  phaseKey,
  channel,
  expand,
  expandAlign,
}: {
  piece: Piece
  phaseKey: string
  channel: Channel | undefined
  expand: "overlay" | "flow"
  expandAlign?: "left" | "right"
}) {
  const fmt = useFormatDateTime()
  const openPreview = usePiecePreview()
  // While the actions dropdown (a portal) is open the pointer leaves the
  // card, so hold the expanded state visible until it closes.
  const [menuOpen, setMenuOpen] = useState(false)
  const todoCount = piece.open_todos.length

  const channelForMenu = channel && {
    slug:                            channel.slug,
    channel_type:                    channel.channel_type,
    credentials_status:              channel.credentials_status,
    publishes_manually:              channel.publishes_manually,
    allow_platform_scheduling:       channel.allow_platform_scheduling,
    platform_scheduling_lead_hours:  channel.platform_scheduling_lead_hours,
    default_to_platform_scheduling:  channel.default_to_platform_scheduling,
    supports_platform_scheduling:    channel.supports_platform_scheduling,
    forces_platform_scheduling:      channel.forces_platform_scheduling,
  }
  const menu = useActionMenu(
    {
      id: piece.id,
      publishing_status: piece.publishing_status,
      channel: piece.channel,
      open_todos: piece.open_todos,
      slot: piece.slot,
    },
    channelForMenu,
  )
  const { fire, dialogs } = useFireAction(
    {
      id: piece.id,
      name: piece.name,
      publishing_status: piece.publishing_status,
      channel: piece.channel,
      open_todos: piece.open_todos,
      slot: piece.slot,
    },
    channelForMenu,
  )

  // Past-slot manual-publish marker — same button as appears in NextActionBox.
  const manualPublishPastDue =
    piece.publishing_status === "scheduled" &&
    piece.slot?.manual_publish === true &&
    new Date(piece.slot.publish_at).getTime() < Date.now()

  const pastDue = isSchedulePastDue(piece.publishing_status, piece.publish_at)
  const text = piece.body_text || piece.display_title || piece.name
  // Scheduled/published pieces show their moment; unscheduled ones show the
  // channel name (the calendar's cards get the day from their cell — here
  // the full date+time carries that context).
  const meta = piece.publish_at ? fmt(piece.publish_at) : (piece.channel_name ?? piece.channel)

  const handleClick = (e: React.MouseEvent) => {
    // Plain click opens the inspect-in-place modal; modifier / non-primary
    // clicks fall through so cmd/ctrl/middle click still opens the full
    // piece page in a new tab.
    if (openPreview && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
      e.preventDefault()
      openPreview(piece.id)
    }
  }

  const headerRight = (
    <>
      {pastDue && (
        <span title="Past due">
          <TriangleAlert className="h-3 w-3 shrink-0 text-signal-display" aria-label="Past due" />
        </span>
      )}
      {manualPublishPastDue && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            fire("mark_published")
          }}
          className="rounded bg-signal-faded px-1 text-[9px] font-semibold text-signal-display hover:bg-signal-faded/80"
          title="Mark this manual-publish piece as published"
        >
          Published?
        </button>
      )}
    </>
  )

  const expandedHeaderRight = menu.primary ? (
    <DropdownMenu onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="More actions"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-ink-muted hover:bg-page/70 hover:text-ink-display"
        >
          <Ellipsis className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[14rem]">
        {[menu.primary, ...menu.secondary].map((item) => (
          <DropdownMenuItem
            key={item.kind}
            onSelect={(e) => {
              e.preventDefault()
              fire(item.kind as ActionKind)
            }}
            className={item.destructive ? "text-danger-display" : undefined}
          >
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : undefined

  const expandedExtras = (
    <>
      {pastDue && (
        <div className="mt-1">
          <PastDueWarning message="Past due — needs rescheduling" className="text-[10px]" />
        </div>
      )}
      {phaseKey === "published" && piece.published_url && (
        <div className="mt-1 flex items-center gap-1 text-[10px]">
          <ExternalLink className="h-3 w-3 shrink-0 text-ink-muted" />
          <a
            href={piece.published_url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="truncate"
          >
            {hostnameOf(piece.published_url)}
          </a>
        </div>
      )}
      {todoCount > 0 && (
        <ul className="mt-1 flex flex-col gap-0.5">
          {piece.open_todos.map((t, i) => (
            <li
              key={`${t.assignee}-${t.kind}-${i}`}
              className="flex items-center gap-1 text-[10px] text-ink-muted"
            >
              <ListTodo className="h-3 w-3 shrink-0" />
              <span className="truncate">{todoLabel(t)}</span>
            </li>
          ))}
        </ul>
      )}
      {(piece.comment_count > 0 || piece.attachment_count > 0) && (
        <div className="mt-1 flex items-center gap-3 text-[10px] text-ink-muted">
          {piece.comment_count > 0 && (
            <span className="inline-flex items-center gap-1">
              <MessageSquare className="h-3 w-3" />
              {piece.comment_count}
            </span>
          )}
          {piece.attachment_count > 0 && (
            <span className="inline-flex items-center gap-1">
              <Paperclip className="h-3 w-3" />
              {piece.attachment_count}
            </span>
          )}
        </div>
      )}
    </>
  )

  return (
    <CompactPieceCard
      as="li"
      href={`/pieces/${piece.id}`}
      onClick={handleClick}
      channelType={piece.channel_type}
      channelColorName={piece.channel_color}
      meta={meta}
      headerRight={headerRight}
      expandedHeaderRight={expandedHeaderRight}
      text={text}
      media={{
        imageUrl: piece.thumbnail_url,
        videoUrl: piece.video_url,
        videoFilename: piece.video_filename,
      }}
      tags={piece.tags}
      expandedExtras={expandedExtras}
      expand={expand}
      expandAlign={expandAlign}
      forceExpanded={menuOpen}
    >
      {dialogs}
    </CompactPieceCard>
  )
}

function hostnameOf(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "")
  } catch {
    return url
  }
}
